import { yupResolver } from "@hookform/resolvers/yup";
import { LoadingButton } from '@mui/lab';
import { Box, Dialog, DialogActions, DialogContent, Grid } from '@mui/material';
import { isArray } from 'lodash';
import { useEffect } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { IndividualService } from '../../../Component/Api/Individual/IndividualService';
import AppAccordion from '../../../Component/Common/Components/Accordion/AppAccordion';
import AppCheckbox from '../../../Component/Common/Components/FormValidationInputs/AppCheckbox';
import { AppDatepicker } from '../../../Component/Common/Components/FormValidationInputs/AppDatepicker';
import AppSelectList from '../../../Component/Common/Components/FormValidationInputs/AppSelectList';
import AppTextInput from '../../../Component/Common/Components/FormValidationInputs/AppTextInput';
import { EEOccupationalLevelList, EmployeeStructureEmployeeTypeList, genderList, provinceList, raceList } from '../../../Component/Common/Constants/Lists/DropdownListOptions';
import { Individual } from '../../../Component/Model/Dto/Individual';
import { PeopleDtos } from '../../../Component/Model/Dto/People';
import { CustomDialogTitle } from '../../Common/Custom/CustomDialogTitle';
import { EmployeeNoPersonValidationSchema, EmployeePersonValidationSchema } from './EmployeeValidationSchema';

interface IEmployeeProps {
    scorecardId: string;
    person?: PeopleDtos.GetPersonRequest | null;
    showEmployeeModal: boolean;
    closeEmployeeModal: () => void;
    afterSaved: (id: number) => void;
    identificationNumber?: string;
    employeeCode?: string;
}

export const EmployeeModal = (props: IEmployeeProps) => {
    const { control, handleSubmit, formState: { isSubmitting }, setValue } = useForm({
        mode: 'all',
        resolver: yupResolver(props.person == null ? EmployeeNoPersonValidationSchema : EmployeePersonValidationSchema)
    });


    const handleDataSubmit = async (data: FieldValues) => {
        try {
            const finalData = data as Individual.IndividualUpdateDto;
            finalData.scorecardId = props.scorecardId;
            finalData.SampledIndividualViewModel = null;
            const result = await IndividualService.SaveIndividual(finalData);
            toast.success(`Employee added successfully`);
            props.afterSaved(result.data)
        } catch (error: any) {
            console.log(error);
            if (isArray(error)) {
                let Errors: string[] = error;
                Errors.forEach((error: string) => {
                    toast.error(error);
                });
            }
        }
    }

    useEffect(() => {
        if (props.person != null) {
            setValue("PersonId", props.person.id);
            setValue("FirstName", props.person.firstName);
            setValue("LastName", props.person.lastName);
            setValue("IdentificationNumber", props.person.identificationNumber);
            setValue("EmployeeCode", props.person.employeeCode);
            setValue("RaceId", props.person.raceId);
            setValue("Gender", props.person.genderId);
            setValue("Disabled", props.person.isDisabled);
            setValue("DateOfBirth", props.person.dateOfBirth);
        } else {

            setValue("PersonId", 0);
            if (props.identificationNumber != null) {
                setValue("IdentificationNumber", props.identificationNumber)
            }
            if (props.employeeCode != null) {
                setValue("EmployeeCode", props.employeeCode)
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return (
        <Box>
            <Dialog disableEnforceFocus open={props.showEmployeeModal} onClose={() => { props.closeEmployeeModal() }} aria-labelledby="form-dialog-title" maxWidth='lg'>
                <CustomDialogTitle id="employee-dialog-title" onClose={() => props.closeEmployeeModal()}>
                    {'Capture New Employee'}
                </CustomDialogTitle>
                <DialogContent>
                    <AppAccordion expandOnDefault={true} label={'Capture New Employee'}>
                        <form id='EmployeeForm' onSubmit={handleSubmit(handleDataSubmit)}>
                            <Grid container spacing={1} mt={1}>
                                <Grid item xs={12} sm={12}>
                                    <AppTextInput control={control} name="IdentificationNumber" label="ID Number" />
                                </Grid>
                                <Grid item xs={12} sm={12}>
                                    <AppTextInput control={control} name="EmployeeCode" label="Employee Code" />
                                </Grid>
                                <Grid item xs={12} sm={12}>
                                    <AppTextInput control={control} name="FirstName" label="Name" />
                                </Grid>
                                <Grid item xs={12} sm={12}>
                                    <AppTextInput control={control} name="LastName" label="Surname" />
                                </Grid>
                                <Grid item xs={12} sm={12}>
                                    <AppSelectList control={control} name="Gender" label="Gender" items={genderList} />
                                </Grid>
                                <Grid item xs={12} sm={12}>
                                    <AppSelectList control={control} name="RaceId" label="Race" items={raceList} />
                                </Grid>
                                <Grid item xs={12} sm={12}>
                                    <AppCheckbox control={control} name='Disabled' label='Disabled' disabled={false} />
                                </Grid>
                                <Grid item xs={12} sm={12}>
                                    <AppCheckbox control={control} name='IsYes' label='Y.E.S Employee' disabled={false} />
                                </Grid>
                                <Grid item xs={12} sm={12}>
                                    <AppCheckbox control={control} name='IsYesAbsorbed' label='Y.E.S Absorbed' disabled={false} />
                                </Grid>
                                <Grid item xs={12} sm={12}>
                                    <AppSelectList control={control} name="Province" label="Province" items={provinceList} />
                                </Grid>
                                <Grid item xs={12} sm={12}>
                                    <AppDatepicker control={control} name="DateOfBirth" label="Date Of Birth" inputFormat='yyyy/MM/dd' minDate={new Date(1900, 1, 1)} />
                                </Grid>
                                {props.person == null ?
                                    <>
                                        <Grid item xs={12} sm={12}>
                                            <AppSelectList control={control} name='OccupationLevelTypeId' label='Occupational Level' items={EEOccupationalLevelList} />
                                        </Grid>
                                        <Grid item xs={12} sm={12}>
                                            <AppSelectList control={control} name='EmployeeTypeId' label='Employment Type' items={EmployeeStructureEmployeeTypeList} />
                                        </Grid>
                                        <Grid item xs={12} sm={12}>
                                            <AppTextInput control={control} name='Position' label='Position' />
                                        </Grid>
                                    </> :
                                    <></>
                                }
                            </Grid>
                        </form>
                    </AppAccordion>
                </DialogContent>
                <DialogActions>
                    <LoadingButton loading={isSubmitting} variant='contained' form='EmployeeForm' type="submit" color='primary'>
                        Save
                    </LoadingButton>
                </DialogActions>
            </Dialog>

        </Box>
    )
}
